import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ConsensoMarketing } from "../components/ConsensoMarketing";
import PaxInput from "../components/PaxInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full h-12 px-4 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2";

export default function PublicBooking() {
  const { rid } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "",
    party_size: 2, time: "", notes: "", consent: false, website: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/restaurant/${rid}/booking`).then((r) => {
      setInfo(r.data);
      document.title = `Prenota — ${r.data.brand.name}`;
    }).catch((e) => setError(e.response?.data?.detail || "Prenotazioni online non disponibili"));
  }, [rid]);

  useEffect(() => {
    if (!date) { setSlots(null); return; }
    axios.get(`${API}/public/restaurant/${rid}/slots`, { params: { date } })
      .then((r) => setSlots(r.data)).catch(() => setSlots(null));
  }, [rid, date]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/restaurant/${rid}/book`, { ...form, date });
      setDone(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore durante la prenotazione. Riprova.");
    } finally { setBusy(false); }
  };

  if (error) return <Centered msg={error} testid="std-error" />;
  if (!info) return <Centered msg="Caricamento..." testid="std-loading" />;

  const { brand } = info;
  const c = brand.colors;
  const today = new Date().toISOString().slice(0, 10);
  const caparra = info.pagamenti?.caparra;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-booking">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-xl mx-auto px-4 py-6 flex justify-center">
          {brand.assets.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.assets.logo}`} alt={brand.name} className="h-24 sm:h-28 w-auto max-w-full object-contain" data-testid="std-logo" />
            : <span className="font-extrabold text-2xl" style={{ color: c.primary }}>{brand.name}</span>}
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
        {done ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center space-y-3" data-testid="std-success">
            <div className="text-4xl">✓</div>
            <h2 className="font-extrabold text-xl" style={{ color: c.primary }}>Richiesta inviata!</h2>
            <p className="text-sm text-zinc-600">
              {new Date(done.reservation.date + "T12:00:00").toLocaleDateString("it-IT",
                { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              {" · "}ore {done.reservation.time} · {done.reservation.party_size} persone.
              Riceverai la conferma dal ristorante.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-extrabold text-2xl sm:text-3xl tracking-tight" style={{ color: c.primary }} data-testid="std-title">
              Prenota il tuo tavolo
            </h1>
            {caparra?.attiva && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="std-caparra">
                Per alcune prenotazioni potrebbe essere richiesta una caparra
                {caparra.importo ? ` (€${caparra.importo})` : ""} alla conferma.
              </div>
            )}
            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="std-form">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-1">Data</label>
                <input required type="date" min={today} value={date} onChange={(e) => { setDate(e.target.value); setForm({ ...form, time: "" }); }}
                  className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="std-data" />
              </div>
              {slots && !slots.aperto && (
                <p className="text-xs text-rose-600 font-semibold" data-testid="std-closed">
                  Il ristorante è chiuso in questa data o non accetta prenotazioni.
                </p>
              )}
              {slots?.aperto && (
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 mb-1">Orario</label>
                  <div className="flex flex-wrap gap-1.5" data-testid="std-slots">
                    {slots.slots.map((s) => (
                      <button key={s} type="button" onClick={() => setForm({ ...form, time: s })} data-testid={`std-slot-${s}`}
                        className={`h-9 px-3 rounded-md text-xs font-bold border ${form.time === s ? "text-white" : "bg-white text-zinc-600"}`}
                        style={form.time === s ? { backgroundColor: c.primary, borderColor: c.primary } : { borderColor: "#E4E4E7" }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Nome *" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="std-nome" />
                <input placeholder="Cognome" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="std-cognome" />
              </div>
              <input required type="tel" placeholder="Telefono *" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="std-telefono" />
              <input type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="std-email" />
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-1">Persone</label>
                <PaxInput value={form.party_size} onChange={(v) => setForm({ ...form, party_size: v })}
                  min={1} max={50} testid="std-persone" />
              </div>
              <textarea placeholder="Note (allergie, richieste speciali…)" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className={`${inputCls} h-auto py-2`} style={{ borderColor: "#E4E4E7" }} data-testid="std-note" />
              <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="hidden" tabIndex={-1} autoComplete="off" data-testid="std-website" />
              <ConsensoMarketing rid={rid} checked={form.consent}
                onChange={(v) => setForm({ ...form, consent: v })} testid="std-consenso" />
              <button disabled={busy || !form.time} className="w-full h-12 rounded-lg text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: c.primary }} data-testid="std-submit">
                {busy ? "Invio in corso..." : "Invia richiesta di prenotazione"}
              </button>
            </form>
          </>
        )}
        <p className="text-center text-[10px] text-zinc-400 pb-4" data-testid="pb-crosspromo">
          Powered by DISHENDA · <a href="/locali" className="underline hover:text-zinc-600 transition-colors">Scopri altri locali nella zona</a>
        </p>
      </main>
    </div>
  );
}

function Centered({ msg, testid }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500 px-6 text-center" data-testid={testid}>
      {msg}
    </div>
  );
}
